import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import { useModalHandle } from '../utils/withModalHandle';
import Button from '../Button';
import Tag from '../Tag';
import { DSIcon } from '../../../base';

import ListingTagModal from './ListingTagModal';

import './ListingChipsContainer.scss';

const MAX_VISIBLE_CHIPS = 8;

const ListingChipsContainer = ({
  chipList = [],
  className = '',
  chipClassName = '',
  maxVisibleChips = MAX_VISIBLE_CHIPS,
  showShowMoreButton = true,
  modalHeaderLabel = null
}) => {
  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

  const allChips = chipList
    .filter(tag => !tag.hideInExperiment && tag.icon)
    .sort((a, b) => !!b.value - !!a.value || (a.order ?? 0) - (b.order ?? 0))
    .map(tag => (
      <Tag
        key={tag.label || tag.name}
        label={tag.name}
        renderLeadingIcon={() => <DSIcon name={tag.icon} size="l" />}
        isSpaced
        size="big"
        colorSchema="light"
        labelClassName={classNames('listing-chips-container__tag', {
          'listing-chips-container__tag--clickable': tag.onClick,
          'listing-chips-container__tag--not-included': !tag.value
        })}
        className={classNames(chipClassName)}
        onClick={tag.onClick}
      />
    ));

  const visibleChips =
    maxVisibleChips === -1 ? allChips : allChips.slice(0, maxVisibleChips);

  const shouldShowShowMoreButton =
    maxVisibleChips !== -1 &&
    showShowMoreButton &&
    allChips.length > maxVisibleChips;

  return (
    <>
      <ListingTagModal
        isOpen={isModalOpen}
        onRequestClose={handleModalClose}
        headerLabel={modalHeaderLabel}
      >
        <div className="listing-chips-container">{allChips}</div>
      </ListingTagModal>
      <div className={classNames('listing-chips-container', className)}>
        {visibleChips}
      </div>
      {shouldShowShowMoreButton && (
        <Button
          className="listing-chips-container__show-features-button"
          color="link"
          onClick={handleModalOpen}
          dataTest="showRemainingFeaturesButton"
        >
          {trans('search.listing.show-all-features', {
            numberOfFeatures: allChips.length
          })}
        </Button>
      )}
    </>
  );
};

ListingChipsContainer.propTypes = {
  chipList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      icon: PropTypes.string,
      description: PropTypes.string,
      descriptionClassName: PropTypes.string,
      order: PropTypes.number,
      hideInExperiment: PropTypes.bool,
      onClick: PropTypes.func
    })
  ),
  className: PropTypes.string,
  chipClassName: PropTypes.string,
  maxVisibleChips: PropTypes.number,
  showShowMoreButton: PropTypes.bool,
  modalHeaderLabel: PropTypes.string
};

export default ListingChipsContainer;
